// 受注数・納品予定グリッドの表示補助をまとめる service です。
export function createDemandGridSupportService(deps) {
    const {
        JUTYU_SUMMARY_COL_ID,
        NOUHIN_SUMMARY_COL_ID,
        isEmpty,
        DicVal,
        parseBoolLike,
        normalizeKakousijiUiString,
        normalizeColumnIdUpper,
        normalizeColumnIdSuffix,
        readRowValueIgnoreCase,
        isDemandPanelInfo
    } = deps;

    const DEMAND_JUTYU_SHOP_DISPLAY_FIELD = "__JUTYU_SJCH_TOKUISAKICD_DISPLAY";
    const DEMAND_JUTYU_CHANNEL_DISPLAY_FIELD = "__JUTYU_SJCH_TORIKEITAISYOSAIKBN_DISPLAY";

    // Grid の補助列かを判定します。
    function isGridAuxColId(colId) {
        const cid = String(colId ?? "");
        return cid === "rowid" || cid === "grip" || cid === "expand";
    }

    // 受注/納品の集計行かを判定します。
    function isDemandSummaryRowKind(kind) {
        const k = String(kind ?? "");
        return k === "summary_label" || k === "summary_total" || k === "subtotal" || k === "total";
    }

    // 受注/納品のセルがレイアウト設定上編集可能か判定します。
    function isDemandCellEditableByLayout(colDef, params) {
        const kind = String(DicVal(DicVal(params, "data", {}), "row_kind", ""));
        if (isDemandSummaryRowKind(kind)) return false;
        const cinfo = DicVal(colDef, "colinfo", null);
        const locked = parseBoolLike(DicVal(cinfo, "locked", false), false);
        const unchange = parseBoolLike(DicVal(cinfo, "unchange", false), false);
        if (locked || unchange) return false;
        const saveed = parseBoolLike(DicVal(cinfo, "saveed", true), true);
        if (!saveed) return false;
        const originalEditable = DicVal(colDef, "editable", null);
        if (typeof originalEditable === "function") return originalEditable(params);
        if (typeof originalEditable === "boolean") return originalEditable;
        return !isEmpty(cinfo);
    }

    function formatDemandCodeNameDisplay(code, name) {
        const normalizedCode = normalizeKakousijiUiString(code);
        if (normalizedCode === "") return "";
        const normalizedName = normalizeKakousijiUiString(name);
        return normalizedName === "" ? normalizedCode : `${normalizedCode}:${normalizedName}`;
    }

    function demandJutyuDisplayFieldId(columnId) {
        const cid = normalizeColumnIdUpper(columnId);
        if (cid === "SJCH_TOKUISAKICD") return DEMAND_JUTYU_SHOP_DISPLAY_FIELD;
        if (cid === "SJCH_TORIKEITAISYOSAIKBN") return DEMAND_JUTYU_CHANNEL_DISPLAY_FIELD;
        return "";
    }

    function normalizeDemandJutyuDisplayRow(row) {
        if (isEmpty(row) || typeof row !== "object") return row;
        const next = { ...row };
        next[DEMAND_JUTYU_SHOP_DISPLAY_FIELD] =
            normalizeKakousijiUiString(readRowValueIgnoreCase(next, "SJCH_TOKUISAKICD_DISPLAY"))
            || formatDemandCodeNameDisplay(
                readRowValueIgnoreCase(next, "SJCH_TOKUISAKICD"),
                readRowValueIgnoreCase(next, "SJCH_TOKUISAKICD_nm")
            );
        next[DEMAND_JUTYU_CHANNEL_DISPLAY_FIELD] =
            normalizeKakousijiUiString(readRowValueIgnoreCase(next, "SJCH_TORIKEITAISYOSAIKBN_DISPLAY"))
            || formatDemandCodeNameDisplay(
                readRowValueIgnoreCase(next, "SJCH_TORIKEITAISYOSAIKBN"),
                readRowValueIgnoreCase(next, "SJCH_TORIKEITAISYOSAIKBN_nm")
            );
        return next;
    }

    function normalizeDemandJutyuDisplayRows(rows, panelType) {
        if (!Array.isArray(rows) || panelType !== "130") return Array.isArray(rows) ? rows : [];
        return rows.map((row) => normalizeDemandJutyuDisplayRow(row));
    }

    // 受注/納品の UI 専用列かを判定します。
    function isDemandUiOnlyColumnId(columnId) {
        const cid = normalizeColumnIdUpper(columnId);
        if (cid === "") return false;
        if (/^-+$/.test(cid)) return true;
        if (/_CD$/.test(cid) || /_NM$/.test(cid) || /_NAME$/.test(cid) || /_DISPLAY$/.test(cid)) return true;
        if (cid === normalizeColumnIdUpper(DEMAND_JUTYU_SHOP_DISPLAY_FIELD) || cid === normalizeColumnIdUpper(DEMAND_JUTYU_CHANNEL_DISPLAY_FIELD)) return true;
        const jutyuIds = Object.values(JUTYU_SUMMARY_COL_ID || {}).map((value) => normalizeColumnIdUpper(value));
        const nouhinIds = Object.values(NOUHIN_SUMMARY_COL_ID || {}).map((value) => normalizeColumnIdUpper(value));
        return jutyuIds.includes(cid) || nouhinIds.includes(cid);
    }

    function isNouhinWhiteCellColumn(colId) {
        if (isDemandUiOnlyColumnId(colId)) return true;
        const cid = String(colId ?? "");
        const sfx = normalizeColumnIdSuffix(cid);
        return ["colorcd", "sizecd", "mdhatchusu", "siiresu", "nouhinyoteisu", "centersu", "part1su", "part2su", "part3su", "part4su", "zansu"].includes(sfx);
    }

    // 受注/納品セル背景色を補正します。
    function resolveDemandCellStyle(originalCellStyle, colDef, params, demandType) {
        const base = typeof originalCellStyle === "function" ? originalCellStyle(params) : originalCellStyle;
        if (demandType !== "131") return base;
        const row = DicVal(params, "data", {});
        const kind = String(DicVal(row, "row_kind", ""));
        if (kind === "summary_label") return base;
        const colId = String(DicVal(colDef, "colId", "") || DicVal(colDef, "field", ""));
        if (!isNouhinWhiteCellColumn(colId)) return base;
        const merged = (base && typeof base === "object") ? { ...base } : {};
        merged.backgroundColor = "#ffffff";
        return merged;
    }

    // 受注数/納品予定のみ、列を内容幅へ寄せます。
    function fitDemandColumns(panelInfo, api) {
        try {
            if (isEmpty(panelInfo) || isEmpty(api)) return;
            if (!isDemandPanelInfo(panelInfo)) return;
            setTimeout(() => {
                try {
                    if (typeof api.autoSizeAllColumns === "function") {
                        api.autoSizeAllColumns(false);
                        return;
                    }
                    if (typeof api.getColumns === "function" && typeof api.autoSizeColumns === "function") {
                        const cols = api.getColumns();
                        const ids = Array.isArray(cols)
                            ? cols
                                .map((col) => {
                                    try {
                                        if (!isEmpty(col) && typeof col.getColId === "function") return col.getColId();
                                    } catch (error) { console.error(error); }
                                    return "";
                                })
                                .filter((value) => value !== "" && !isGridAuxColId(value))
                            : [];
                        if (ids.length !== 0) api.autoSizeColumns(ids, false);
                    }
                } catch (error) { console.error(error); }
            }, 0);
        } catch (error) { console.error(error); }
    }

    return {
        isGridAuxColId,
        isDemandSummaryRowKind,
        isDemandCellEditableByLayout,
        demandJutyuDisplayFieldId,
        normalizeDemandJutyuDisplayRows,
        isDemandUiOnlyColumnId,
        resolveDemandCellStyle,
        fitDemandColumns
    };
}
